/**
 * Wallet Earnings Repository
 *
 * Handles CRUD operations and real-time listeners for earnings documents
 * stored at users/{uid}/wallet/earnings/{earningId}
 *
 * Records are immutable (write-once) - no update method provided.
 */

import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  deleteDoc,
  where,
} from 'firebase/firestore';
import { Paths, Page, PaginationOptions, withBackoff, setDoc, getDoc } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import type { Earning, EarningStatus } from '../types/earnings';
import {
  EarningDoc,
  toEarningDoc,
  fromEarningDoc,
  generateWalletItemId,
} from '../types/walletFirestore';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// ============================================================================
// Types
// ============================================================================

export interface CreateEarningInput {
  /** Summary description */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Member ID */
  memberId: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'pending') */
  status?: EarningStatus;
}

export interface ListEarningsOptions extends PaginationOptions {
  /** Filter by status */
  status?: EarningStatus;
}

// ============================================================================
// Credit deposits shown as earnings (R18, owner directive 2026-08-09)
// ============================================================================

/**
 * Access-code trial credits are written to wallet/summary/TRANSACTIONS (they
 * are deposits), never to the earnings subcollection — so the earnings section
 * showed a trial account only its absorbed $100 welcome seed while the $1,000
 * tier credit lived on a different surface under a different frame. Read those
 * credit deposits and present them AS earnings, reusing the transaction's own
 * summary string verbatim ("trial credit — N pr0 (access code)…") so both
 * surfaces name the same money the same way.
 *
 * Index-free by design: a bounded orderBy-only scan of recent transactions,
 * filtered client-side. Credits are rare; 100 recent transactions is years of
 * history for the accounts this affects. Best-effort — a failed read returns
 * [] and the earnings list renders exactly as before.
 */
const CREDIT_DEPOSIT_SCAN_LIMIT = 100;
const CREDIT_DEPOSIT_TAGS = ['access_code_credit', 'trial_credit'];

export async function fetchCreditDepositEarnings(uid: string): Promise<Earning[]> {
  try {
    const col = Paths.walletTransactionsCol(uid);
    const q = query(col, orderBy('timestamp', 'desc'), limit(CREDIT_DEPOSIT_SCAN_LIMIT));
    const snap = await withBackoff(() => getDocs(q));
    return snap.docs
      .map((d) => d.data() as Record<string, any>)
      .filter((tx) =>
        tx?.type === 'deposit' &&
        Array.isArray(tx.tags) &&
        tx.tags.some((t: unknown) => CREDIT_DEPOSIT_TAGS.includes(String(t))))
      .map((tx) => ({
        id: String(tx.id || ''),
        timestamp: tx.timestamp?.toMillis?.() ?? 0,
        tags: Array.isArray(tx.tags) ? tx.tags.map(String) : [],
        // Verbatim — the whole point is that both surfaces say the same thing.
        summary: String(tx.summary || 'trial credit'),
        source: 'access-code',
        memberId: String(tx.member_id || ''),
        amountUsd: Number(tx.amount_usd) || 0,
        amountOlv: Number(tx.amount_olv) || 0,
        olvRateAtTime: Number(tx.olv_rate_at_time) || 0.001,
        status: 'completed' as const,
      }))
      .filter((e) => e.id && e.timestamp > 0);
  } catch {
    return [];
  }
}

/** Merge credit deposits into an earnings list: dedupe by id, newest first. */
export function mergeCreditDeposits(earnings: Earning[], deposits: Earning[]): Earning[] {
  if (deposits.length === 0) return earnings;
  const seen = new Set(earnings.map((e) => e.id));
  const merged = [...earnings, ...deposits.filter((d) => !seen.has(d.id))];
  merged.sort((a, b) => b.timestamp - a.timestamp);
  return merged;
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new earning record (instant write, no debounce)
 * @returns The created earning with generated ID
 */
export async function createEarning(uid: string, input: CreateEarningInput): Promise<Earning> {
  addUserScopedWriteGuard(uid, 'createEarning');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  const earning: Earning = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    source: input.source,
    memberId: input.memberId,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'pending',
  };

  const docData = {
    ...toEarningDoc(earning),
    created_at: serverTimestamp(),
  };

  const ref = Paths.walletEarningDoc(uid, id);
  await withBackoff(() => setDoc(ref, docData));

  return earning;
}

/**
 * Get a single earning by ID
 */
export async function getEarning(uid: string, id: string): Promise<Earning | null> {
  const ref = Paths.walletEarningDoc(uid, id);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return null;
  return fromEarningDoc(snap.data() as EarningDoc);
}

/**
 * List earnings with pagination (newest first)
 */
export async function listEarnings(
  uid: string,
  opts?: ListEarningsOptions
): Promise<Page<Earning>> {
  const col = Paths.walletEarningsCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts?.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Always order by timestamp descending (newest first)
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  if (opts?.limit) {
    parts.push(limit(opts.limit));
  }

  // Build base query
  let q = query(col, ...parts);

  // Apply cursor if provided
  if (opts?.startAfter) {
    const { startAfter: fbStartAfter } = await import('firebase/firestore');
    q = query(q, fbStartAfter(opts.startAfter as DocumentSnapshot));
  }

  const snap = await withBackoff(() => getDocs(q));
  const items = snap.docs.map((d) => fromEarningDoc(d.data() as EarningDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

  // R18: credit deposits join the FIRST page only (no cursor) — they are
  // few, recent, and merging them into every page would duplicate them.
  if (!opts?.startAfter && !opts?.status) {
    return { items: mergeCreditDeposits(items, await fetchCreditDepositEarnings(uid)), nextCursor };
  }
  return { items, nextCursor };
}

/**
 * Delete an earning (admin only, if ever needed)
 */
export async function deleteEarning(uid: string, id: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'deleteEarning');
  const ref = Paths.walletEarningDoc(uid, id);
  await withBackoff(() => deleteDoc(ref));
}

/**
 * Delete all earnings with a specific tag
 * @param uid User ID
 * @param tag Tag to filter by (e.g., "test example")
 * @returns Number of deleted earnings
 */
export async function deleteEarningsByTag(uid: string, tag: string): Promise<number> {
  addUserScopedWriteGuard(uid, 'deleteEarningsByTag');

  const col = Paths.walletEarningsCol(uid);
  const q = query(col, where('tags', 'array-contains', tag));
  const snap = await withBackoff(() => getDocs(q));

  let deletedCount = 0;
  for (const doc of snap.docs) {
    await withBackoff(() => deleteDoc(doc.ref));
    deletedCount++;
  }

  return deletedCount;
}

// ============================================================================
// Real-time Listeners
// ============================================================================

/**
 * Subscribe to earnings with real-time updates
 * @param uid User ID
 * @param opts Options including limit and optional status filter
 * @param onData Callback with updated earnings list and cursor
 * @param onError Optional error callback
 * @returns Unsubscribe function
 */
export function onEarningsSnapshot(
  uid: string,
  opts: { limit: number; status?: EarningStatus },
  onData: (items: Earning[], nextCursor: DocumentSnapshot | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const col = Paths.walletEarningsCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Order by timestamp descending
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  parts.push(limit(opts.limit));

  const q = query(col, ...parts);

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => fromEarningDoc(d.data() as EarningDoc));
      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      if (opts.status) {
        onData(items, nextCursor);
        return;
      }
      // R18: fold credit deposits in on every snapshot tick. Redemption
      // mutates the welcome-credit earning (absorbed_into_trial), so the
      // earnings snapshot re-fires right when a new deposit appears — the
      // refetch below keeps the two in lockstep without a second listener.
      fetchCreditDepositEarnings(uid)
        .then((deposits) => onData(mergeCreditDeposits(items, deposits), nextCursor))
        .catch(() => onData(items, nextCursor));
    },
    (error) => {
      console.error('Earnings snapshot error:', error);
      onError?.(error);
    }
  );
}
